import * as React from 'react';
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { Spinner } from '@fluentui/react-components';
import Cookies from 'js-cookie';
import { defineMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useUpdateUserMutation, useUserQuery } from '@foundershub/api-client';
import { ErrorBoundary } from '../../components';
import { DrawerProvider } from '../../components/FoundersHubUserOverlayDrawer';
import ExpertLayout from '../../layouts/ExpertLoggedIn';
import AccountRequiredRedirectLayout from '../../layouts/LoggedIn';
import LoggedOutLayout from '../../layouts/LoggedOut';
import ReferralLayout from '../../layouts/Referral';
import { useCookieConsent } from '../../modules/cookie-consent';
import { useUpdateLocale } from '../../modules/intl';
import type { Locale } from '../../modules/intl/locales';
import { getLocaleHtml } from '../../modules/intl/locales';
import redirect, { makeMatchRoute } from '../../modules/redirect';
import { setUser, unsetUser } from '../../modules/telemetry';
import {
  LoggedInExpertNotFound,
  loggedInExpertRedirects,
  loggedInExpertRoutes,
  loggedInRedirects,
  loggedInRoutes,
  loggedOutRedirects,
  loggedOutRedirects2,
  loggedOutRoutes,
  loggedOutRoutes2,
  NotFoundLoggedIn,
  NotFoundLoggedOut,
  Routes,
} from '../../routes';
import { useFeatureFlagEnabled } from '../../store';
import { bootstrap } from '..';

initializeIcons();

export function App() {
  const { data: user, isLoading, error } = useUserQuery();
  const history = useHistory();
  const [userLocaleFromQueryParam] = React.useState(history.location.search.includes('lang='));

  // Remove after feature flag turned on
  const gated_application = useFeatureFlagEnabled('gated_application');
  let loggedOutReds = loggedOutRedirects;
  let loggedOutRts = loggedOutRoutes;
  if (gated_application) {
    loggedOutReds = loggedOutRedirects2;
    loggedOutRts = loggedOutRoutes2;
  }

  const location = useLocation();
  const setUserLocale = useUpdateLocale();
  const { siteConsent } = useCookieConsent();
  // Added for 1DS tracking (Only capture on initial load)
  const [mcId] = React.useState<string | null>(new URLSearchParams(window.location.search).get('WT.mc_id'));
  const intl = useIntl();
  let redirects = loggedOutReds;
  let routes = loggedOutRts;
  let notFound = user ? NotFoundLoggedIn : NotFoundLoggedOut;
  let fallback = '/login';
  if (loggedInExpertRoutes.some(makeMatchRoute(history.location.pathname))) {
    fallback = '/login/expert';
  }
  let antiRoutes = [...loggedInRoutes, ...loggedInExpertRoutes];
  const [updateUserDetail] = useUpdateUserMutation();
  let routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
  let parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;
  const isApplyPath = history.location.pathname.includes('/apply');
  const isLogoutPath = history.location.pathname.includes('/logout');
  const isReferralPath = history.location.pathname.includes('/referral');
  if (isReferralPath && gated_application) {
    parentLayout = <ReferralLayout>{routesComponent}</ReferralLayout>;
  }
  if (user && (isApplyPath || isLogoutPath)) {
    redirects = loggedInRedirects;
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;
  }
  if (user && !isApplyPath && !isLogoutPath) {
    fallback = '/benefits';
    redirects = loggedInRedirects;
    routes = loggedInRoutes;
    antiRoutes = loggedOutRts;
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <AccountRequiredRedirectLayout>{routesComponent}</AccountRequiredRedirectLayout>;
    if (user?.is_expert) {
      redirects = loggedInExpertRedirects;
      routes = loggedInExpertRoutes;
      notFound = LoggedInExpertNotFound;
      routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
      parentLayout = <ExpertLayout> {routesComponent} </ExpertLayout>;
    }
  }

  React.useEffect(() => {
    if (user) {
      setUser(user);
    }
    if (error) {
      unsetUser();
    }
  }, [user && user.email, error]);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);

    if (user && user.locale) {
      setUserLocale(user.locale as Locale);
    }
    if (urlParams.has('lang')) {
      const lang = getLocaleHtml(urlParams.get('lang') as Locale);
      setUserLocale(lang);
    }
    // n.b. intentionally not depending on location.search to preserve ?lang= across all routes in a session
  }, [userLocaleFromQueryParam, user && user.locale]);

  React.useEffect(() => {
    if (user) {
      const msfpc_data = Cookies.get('MSFPC');

      // MSFPC GUID has no dashes
      const msfpc_regex = /GUID=([a-z0-9]{32})/;
      const msfpc_id = !!msfpc_data && msfpc_data.match(msfpc_regex)?.at(1);
      if (user.msfpc_id == null && msfpc_id) {
        updateUserDetail({ msfpc_id });
      }
    }
  }, [user]);

  React.useEffect(() => {
    // Added for 1DS tracking
    if (siteConsent) {
      if (!siteConsent.isConsentRequired || siteConsent.getConsent().Analytics) {
        if (mcId !== null && location.pathname !== '/apply/submitted') {
          Cookies.set('mc_id', mcId, { expires: 365 });
        }
      } else {
        Cookies.remove('mc_id');
      }
    }
  }, [siteConsent?.getConsentFor('Analytics')]);

  const parameterizedRedirect = redirect(history.location.pathname, antiRoutes, redirects, fallback);

  if (parameterizedRedirect != null) {
    redirects.push(parameterizedRedirect);
  }

  return (
    <ErrorBoundary type="full">
      <DrawerProvider>
        {isLoading && !error && !user ? (
          <Spinner label={intl.formatMessage(loadingLabel)} labelPosition="below" />
        ) : (
          <div>{parentLayout}</div>
        )}
      </DrawerProvider>
    </ErrorBoundary>
  );
}

const loadingLabel = defineMessage({ defaultMessage: 'Loading user info', description: 'Label for loading spinner' });

bootstrap(App);
